import { useEffect, useState } from 'react';
import { Title, useCreatePath } from 'react-admin';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { FEATURE_INQUIRY_OUTCOMES, fetchQuoteHistory, moneyText, toErrorMessage, type QuoteHistory } from './inquiriesApi';

const Tile = ({ label, value, testId }: { label: string; value: string | number; testId: string }) => (
  <Card variant="outlined" sx={{ minWidth: 180 }} data-testid={testId}>
    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </CardContent>
  </Card>
);

/**
 * Historia wycen i wyników (makieta „Historia wycen”; 26.10.2026, P3,
 * `inquiryOutcomes`, S-26.10-10). Każda wycena — także zastąpione — z ceną,
 * wynikiem i powodem; liczniki: przyjęte, odrzucone, bez odpowiedzi
 * i najczęstszy powód odrzucenia.
 */
export const InquiryHistory = () => {
  const on = useFeatureState(FEATURE_INQUIRY_OUTCOMES);
  const createPath = useCreatePath();
  const [data, setData] = useState<QuoteHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (on !== true) return;
    let alive = true;
    fetchQuoteHistory()
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(toErrorMessage(e)));
    return () => {
      alive = false;
    };
  }, [on]);

  if (on === undefined) return <FeaturesLoading />;
  if (!on) return null;

  return (
    <Box sx={{ p: 1 }} data-testid="inquiry-history">
      <Title title="Historia wycen" />
      {error && <Alert severity="error">{error}</Alert>}
      {data && (
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Tile label="Przyjęte" value={data.counters.accepted} testId="inquiry-history-accepted" />
            <Tile label="Odrzucone" value={data.counters.rejected} testId="inquiry-history-rejected" />
            <Tile label="Bez odpowiedzi" value={data.counters.noResponse} testId="inquiry-history-no-response" />
            <Tile
              label="Najczęstszy powód odrzucenia"
              value={data.counters.topRejectionReason ?? '—'}
              testId="inquiry-history-top-reason"
            />
          </Box>
          <Card variant="outlined">
            <CardContent sx={{ overflowX: 'auto' }}>
              {data.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Jeszcze nie ma żadnej wyceny.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Wycena</TableCell>
                      <TableCell>Klient</TableCell>
                      <TableCell>Relacja</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Cena</TableCell>
                      <TableCell>Wynik</TableCell>
                      <TableCell>Powód</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.items.map((q) => (
                      <TableRow key={q.id} data-testid="inquiry-history-row">
                        <TableCell>
                          <Link to={createPath({ resource: 'inquiries', type: 'show', id: q.inquiryId })}>{q.ref}</Link>
                        </TableCell>
                        <TableCell>{q.clientName ?? '—'}</TableCell>
                        <TableCell>{q.relation}</TableCell>
                        <TableCell>{dateTimeNoSeconds(q.proposedAt)}</TableCell>
                        <TableCell>{moneyText(q.price, q.currency)}</TableCell>
                        <TableCell>
                          {q.resultLabel ? (
                            <Chip size="small" color={q.result === 'accepted' ? 'success' : 'default'} label={q.resultLabel} />
                          ) : q.superseded ? (
                            <Chip size="small" variant="outlined" label="zastąpiona" />
                          ) : q.offerSent ? (
                            'czeka na wynik'
                          ) : q.approved ? (
                            'zatwierdzona'
                          ) : (
                            'czeka na zatwierdzenie'
                          )}
                        </TableCell>
                        <TableCell>{q.resultReason ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};
